import React from "react";
import FormLayout from '../FormLayout';
import TextField from './TextField';
import { Field } from 'redux-form';
import Utils from '../../services/Utils';

/** Class representing a Form list field. */
class ListField extends TextField {

    constructor(properties) {
        super({
            ...(ListField.getDefaultOptions()),
            ...properties,
        });

        this.type = FormLayout.LIST;

        // sort choices by ascending position
        this.choices = (this.choices || []).sort((a, b) => a.position - b.position);
    }

    /**
     * Get this field default options
     */
    static getDefaultOptions() {
        const properties = super.getDefaultOptions();

        properties.choices  = [];
        properties.expanded = false;
        properties.multiple = false;

        return properties;
    }

    /**
     * Return the list of options this field handles
     */
    getHandledOptions() {
        return super.getHandledOptions().concat([
            FormLayout.OPTION_CHOICES,
            FormLayout.OPTION_EXPANDED,
            FormLayout.OPTION_MULTIPLE,
        ]);
    }

    /**
     * Return this field preview input
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {Bool} inPreview
     *                 - {String} lang
     *                 - {Object} setting
     *                 - {Any} value
     *                 - {Object} resources
     */
    getInput(props) {
        const { lang } = props;
        const base = this.reduxFieldName(props);
        const choiceLabel = (choice) => Utils.localized_or_fallback(choice?.label, lang, '');

        if (this.expanded)
            return this.choices.map(choice => (
                <div key={ choice.id } className="listFieldChoice">
                    {this.multiple ? (
                        <Field
                            component="input"
                            type="checkbox"
                            className="form-control"
                            name={ `${base}[${this.getChoiceSubmittedValue(choice)}]` }
                            style={{ height: '25px', width: '25px' }}
                        />
                    ) : (
                        <Field
                            component="input"
                            type="radio"
                            className="form-control"
                            name={ base }
                            value={ this.getChoiceSubmittedValue(choice) }
                            required={this.htmlRequired(props)}
                            style={{ height: '25px', width: '25px' }}
                        />
                    )}
                    <label>{choiceLabel(choice)}</label>
                </div>
            ));

        const placeholderText = Utils.localized_or_fallback(
            this.placeholder, lang, '---'
        );

        return (
            <Field
                component="select"
                name={base}
                multiple={!!this.multiple}
                required={this.htmlRequired(props)}
                className="form-control"
            >
                <option value="">
                    {placeholderText}
                </option>
                { this.choices?.map(choice => (
                    <option key={ choice.id } value={ this.getChoiceSubmittedValue(choice) }>
                        {choiceLabel(choice)}
                    </option>
                ))}
            </Field>
        );
    }

    getChoiceSubmittedValue(choice) {
        if (choice?.value !== undefined && choice?.value !== null && choice.value !== '')
            return `${choice.value}`;

        if (choice?.id !== undefined && choice?.id !== null)
            return `${choice.id}`;

        if (choice?.ref_id !== undefined && choice?.ref_id !== null)
            return `${choice.ref_id}`;

        return '';
    }

    /**
     * Render a submitted value using the corresponding choice label.
     *
     * Backward compatibility:
     * - new forms can submit choice.value
     * - legacy forms may still submit choice.id / choice.ref_id
     */
    renderValue(value, context) {
        if (Array.isArray(value))
            return value.map(v => this.renderValue(v, context)).join(', ');

        const lang = context?.lang;
        const choices = this.choices || [];
        const scalar_value = value != null ? `${value}` : value;

        const matched_choice = choices.find(choice => {
            const choice_value = choice?.value != null ? `${choice.value}` : null;
            const choice_id = choice?.id != null ? `${choice.id}` : null;
            const choice_ref_id = choice?.ref_id != null ? `${choice.ref_id}` : null;

            return (
                (choice_value !== null && scalar_value === choice_value) ||
                (choice_id !== null && scalar_value === choice_id) ||
                (choice_ref_id !== null && scalar_value === choice_ref_id)
            );
        });

        if (!matched_choice)
            return super.renderValue(value, context);

        return Utils.localized_or_fallback(matched_choice?.label, lang, '')
            || super.renderValue(matched_choice?.label, context);
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'list';
    }
}

export default ListField;
